import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Clock,
  Calendar,
  Target,
  CheckCircle,
  X,
  Settings,
  Volume2,
  VolumeX,
  Smartphone,
  Mail,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Input } from '../../ui/input';
import {
  getStudentSchedule,
  getStudentLearningGoals,
  type StudentStudySchedule,
  type StudentLearningGoal
} from '../../../services/studentEnhancementsService';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';

interface StudyRemindersProps {
  studentId: string;
}

interface ReminderSettings {
  enabled: boolean;
  soundEnabled: boolean;
  emailEnabled: boolean;
  pushEnabled: boolean;
  advanceTime: number; // minutes avant l'événement
  dailyGoalReminder: boolean;
  weeklyGoalReminder: boolean;
  studyStreakReminder: boolean;
}

interface StudyNotification {
  id: string;
  type: 'schedule' | 'goal' | 'streak' | 'general';
  title: string;
  message: string;
  scheduledFor: Date;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high';
  actionUrl?: string;
}

const StudyReminders: React.FC<StudyRemindersProps> = ({ studentId }) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [notifications, setNotifications] = useState<StudyNotification[]>([]);
  const [upcomingSchedule, setUpcomingSchedule] = useState<StudentStudySchedule[]>([]);
  const [activeGoals, setActiveGoals] = useState<StudentLearningGoal[]>([]);
  const [settings, setSettings] = useState<ReminderSettings>({
    enabled: true,
    soundEnabled: true,
    emailEnabled: false,
    pushEnabled: true,
    advanceTime: 15,
    dailyGoalReminder: true,
    weeklyGoalReminder: true,
    studyStreakReminder: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadReminderData();
    // Charger les paramètres depuis le localStorage
    loadSettings();
    
    // Vérifier les notifications périodiquement
    const interval = setInterval(checkForNotifications, 60000); // Chaque minute
    
    return () => clearInterval(interval);
  }, [studentId, tenant?.id]);

  const loadReminderData = async () => {
    if (!studentId || !tenant?.id) return;

    setIsLoading(true);
    try {
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      const [scheduleData, goalsData] = await Promise.all([
        getStudentSchedule(
          studentId,
          now.toISOString().split('T')[0],
          tomorrow.toISOString().split('T')[0],
          tenant.id
        ),
        getStudentLearningGoals(studentId, true, undefined, tenant.id)
      ]);

      setUpcomingSchedule(scheduleData.filter(item => !item.is_completed));
      setActiveGoals(goalsData);

      // Générer les notifications
      generateNotifications(scheduleData, goalsData);

    } catch (error) {
      console.error('Erreur lors du chargement des données de rappel:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = () => {
    const savedSettings = localStorage.getItem('study_reminder_settings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  };

  const saveSettings = (newSettings: ReminderSettings) => {
    setSettings(newSettings);
    localStorage.setItem('study_reminder_settings', JSON.stringify(newSettings));
  };

  const generateNotifications = (schedule: StudentStudySchedule[], goals: StudentLearningGoal[]) => {
    const newNotifications: StudyNotification[] = [];
    const now = new Date();

    // Notifications pour les sessions programmées
    schedule.forEach(item => {
      if (item.is_completed) return;

      const scheduledDate = new Date(item.scheduled_date);
      if (item.scheduled_time) {
        const [hours, minutes] = item.scheduled_time.split(':');
        scheduledDate.setHours(parseInt(hours), parseInt(minutes));
      }

      const timeDiff = scheduledDate.getTime() - now.getTime();
      const minutesUntil = Math.floor(timeDiff / (1000 * 60));

      // Notification 15 minutes avant
      if (minutesUntil <= settings.advanceTime && minutesUntil > 0) {
        newNotifications.push({
          id: `schedule_${item.id}`,
          type: 'schedule',
          title: 'Session d\'étude programmée',
          message: `"${item.title}" commence dans ${minutesUntil} minutes`,
          scheduledFor: new Date(scheduledDate.getTime() - settings.advanceTime * 60000),
          isRead: false,
          priority: 'high'
        });
      }

      // Notification pour session en retard
      if (minutesUntil < -30) { // Plus de 30 minutes de retard
        newNotifications.push({
          id: `late_${item.id}`,
          type: 'schedule',
          title: 'Session manquée',
          message: `Vous avez manqué votre session "${item.title}"`,
          scheduledFor: now,
          isRead: false,
          priority: 'medium'
        });
      }
    });

    // Notifications pour les objectifs
    goals.forEach(goal => {
      if (goal.is_completed) return;

      const progress = (goal.current_value / goal.target_value) * 100;
      
      // Objectif proche de l'échéance
      if (goal.target_date) {
        const targetDate = new Date(goal.target_date);
        const daysUntil = Math.floor((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysUntil <= 3 && daysUntil > 0 && progress < 80) {
          newNotifications.push({
            id: `goal_deadline_${goal.id}`,
            type: 'goal',
            title: 'Objectif proche de l\'échéance',
            message: `"${goal.title}" se termine dans ${daysUntil} jour(s) - ${Math.round(progress)}% complété`,
            scheduledFor: now,
            isRead: false,
            priority: 'high'
          });
        }
      }

      // Objectif quotidien non atteint
      if (goal.goal_type === 'daily' && settings.dailyGoalReminder) {
        const today = now.toDateString();
        const lastUpdate = new Date(goal.updated_at).toDateString();
        
        if (today !== lastUpdate && now.getHours() >= 18) { // Après 18h
          newNotifications.push({
            id: `daily_goal_${goal.id}`,
            type: 'goal',
            title: 'Objectif quotidien',
            message: `N'oubliez pas votre objectif quotidien: "${goal.title}"`,
            scheduledFor: now,
            isRead: false,
            priority: 'medium'
          });
        }
      }
    });

    // Notification de série d'étude
    if (settings.studyStreakReminder) {
      // Cette logique nécessiterait des données sur les séries d'étude
      // Pour l'instant, on peut ajouter une notification générale
      const lastStudyTime = localStorage.getItem('last_study_time');
      if (lastStudyTime) {
        const lastStudy = new Date(lastStudyTime);
        const hoursSinceLastStudy = (now.getTime() - lastStudy.getTime()) / (1000 * 60 * 60);
        
        if (hoursSinceLastStudy > 48) { // Plus de 2 jours
          newNotifications.push({
            id: 'study_streak',
            type: 'streak',
            title: 'Maintenez votre rythme !',
            message: 'Cela fait un moment que vous n\'avez pas étudié. Reprenez votre élan !',
            scheduledFor: now,
            isRead: false,
            priority: 'low'
          });
        }
      }
    }

    setNotifications(newNotifications);
  };

  const checkForNotifications = () => {
    if (!settings.enabled) return;

    const now = new Date();
    const activeNotifications = notifications.filter(
      notif => !notif.isRead && notif.scheduledFor <= now
    );

    activeNotifications.forEach(notif => {
      showNotification(notif);
    });
  };

  const showNotification = (notification: StudyNotification) => {
    // Afficher le toast
    toast({
      title: notification.title,
      description: notification.message,
      variant: notification.priority === 'high' ? 'destructive' : 'default'
    });

    // Son de notification
    if (settings.soundEnabled) {
      playNotificationSound();
    }

    // Marquer comme lu
    markAsRead(notification.id);
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignorer les erreurs de lecture audio
      });
    } catch (error) {
      // Ignorer les erreurs
    }
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const dismissNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high': return 'border-red-500/50 bg-red-900/20';
      case 'medium': return 'border-yellow-500/50 bg-yellow-900/20';
      case 'low': return 'border-blue-500/50 bg-blue-900/20';
      default: return 'border-white/20 bg-white/5';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'schedule': return <Calendar className="text-blue-400" size={16} />;
      case 'goal': return <Target className="text-green-400" size={16} />;
      case 'streak': return <CheckCircle className="text-purple-400" size={16} />;
      default: return <Bell className="text-gray-400" size={16} />;
    }
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (isLoading) {
    return (
      <Card className="bg-black/20 border-white/10">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-white/10 rounded w-1/4"></div>
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 bg-white/10 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-black/20 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center">
            <Bell className="mr-2 text-blue-400" size={24} />
            Rappels d'étude
            {unreadCount > 0 && (
              <Badge className="ml-2 bg-red-600 text-white">
                {unreadCount}
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="text-white/70 hover:text-white"
          >
            <Settings size={16} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Paramètres */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10"
            >
              <h3 className="text-white font-semibold mb-4">Paramètres des rappels</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="enabled" className="text-white/80">
                    Activer les rappels
                  </Label>
                  <Switch
                    id="enabled"
                    checked={settings.enabled}
                    onCheckedChange={(checked) => 
                      saveSettings({ ...settings, enabled: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="sound" className="text-white/80">
                    Son de notification
                  </Label>
                  <Switch
                    id="sound"
                    checked={settings.soundEnabled}
                    onCheckedChange={(checked) => 
                      saveSettings({ ...settings, soundEnabled: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="daily-goal" className="text-white/80">
                    Rappels objectifs quotidiens
                  </Label>
                  <Switch
                    id="daily-goal"
                    checked={settings.dailyGoalReminder}
                    onCheckedChange={(checked) => 
                      saveSettings({ ...settings, dailyGoalReminder: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-white/80">
                    Rappel avant session
                  </Label>
                  <Select
                    value={settings.advanceTime.toString()}
                    onValueChange={(value) => 
                      saveSettings({ ...settings, advanceTime: parseInt(value) })
                    }
                  >
                    <SelectTrigger className="w-24 bg-white/5 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 min</SelectItem>
                      <SelectItem value="10">10 min</SelectItem>
                      <SelectItem value="15">15 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notifications actives */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white">
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </h3>

          {notifications.length === 0 ? (
            <div className="text-center py-8 text-white/70">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-400/50" />
              <p>Aucune notification pour le moment</p>
              <p className="text-sm text-white/50">
                Nous vous préviendrons de vos prochaines sessions d'étude
              </p>
            </div>
          ) : (
            notifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border transition-all ${
                  getPriorityColor(notification.priority)
                } ${notification.isRead ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getTypeIcon(notification.type)}
                    <div className="flex-1">
                      <h4 className="text-white font-medium mb-1">
                        {notification.title}
                      </h4>
                      <p className="text-white/80 text-sm mb-2">
                        {notification.message}
                      </p>
                      <div className="text-xs text-white/50">
                        {formatTime(notification.scheduledFor)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      variant="outline" 
                      className="border-white/20 text-white/70 text-xs"
                    >
                      {notification.priority}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissNotification(notification.id)}
                      className="text-white/50 hover:text-white p-1"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Sessions à venir */}
        {upcomingSchedule.length > 0 && (
          <div className="mt-6 pt-4 border-t border-white/10">
            <h3 className="text-lg font-semibold text-white mb-3">
              Prochaines sessions
            </h3>
            <div className="space-y-2">
              {upcomingSchedule.slice(0, 3).map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Calendar className="text-blue-400" size={16} />
                    <div>
                      <p className="text-white text-sm font-medium">
                        {item.title}
                      </p>
                      <p className="text-white/60 text-xs">
                        {new Date(item.scheduled_date).toLocaleDateString('fr-FR')}
                        {item.scheduled_time && ` à ${formatTime(new Date(`2000-01-01T${item.scheduled_time}`))}`}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-white/20 text-white/70 text-xs">
                    {item.duration_minutes} min
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StudyReminders;
